import { TFunction } from "i18next";
import { Base } from "../../framework/base";
import { EnumHelper, WorkShiftTimeSlotState } from "../common/enums";
import { WorkShiftTimeSlotItem } from "./workShiftTimeSlotItem";
import { WorkTimeSaveData } from "./workTimeSaveData";

/**
 * Single source of truth for building a WorkTimeSaveData from a WorkShiftTimeSlotItem row.
 * Used by inline grid save, bulk edit, and form overlap dialog.
 */
export function buildWorkTimeSaveData(
    row: WorkShiftTimeSlotItem,
    employeeId?: string
): WorkTimeSaveData {
    const isNew = !row.id || row.id === "new";
    return {
        id: isNew ? "" : row.id,
        rowId: isNew ? "" : row.rowId,
        employeeId: row.employeeId ?? employeeId,
        vehicleId: row.vehicleId,
        workTimeTypeId: row.workTimeTypeId,
        salaryRowTypeId: row.salaryRowTypeId,
        costCenterId: row.costCenterId,
        workOrderId: row.workOrderId,
        customerId: row.customerId,
        comment: row.comment ?? "",
        startDate: row.startDate
            ? Base.dateStrToOriginalTimezoneJsonDateTime(row.startDate)
            : null,
        endDate: row.endDate
            ? Base.dateStrToOriginalTimezoneJsonDateTime(row.endDate)
            : null,
        timeZoneName: row.timeZoneName,
        isCustomType: row.isCustomType ?? false,
    };
}

const EDITABLE_FIELDS = new Set([
    "startDate",
    "endDate",
    "customerId",
    "vehicleId",
    "salaryRowTypeId",
    "comment",
    "timeZoneName",
    "workTimeTypeId",
    "costCenterId",
]);

/**
 * Single source of truth for whether a field is editable on a given row.
 * Used by isCellEditable callback and bulk edit filtering.
 */
export function canEditField(
    row: WorkShiftTimeSlotItem,
    fieldKey: string,
    options?: { workTimeTypesAvailable?: boolean }
): boolean {
    if (
        EnumHelper.isEqual(row.state as number, WorkShiftTimeSlotState.Locked)
    ) {
        return false;
    }
    if (!EDITABLE_FIELDS.has(fieldKey)) {
        return false;
    }
    if (fieldKey === "workTimeTypeId") {
        return !row.isCustomType && (options?.workTimeTypesAvailable ?? true);
    }
    if (fieldKey === "costCenterId") {
        return !row.workOrderId;
    }
    return true;
}

/**
 * Shared row validation. Returns an array of field-level errors.
 * Used by inline grid validateRow and bulk edit pre-save validation.
 */
export function validateWorkTimeRow(
    row: Pick<
        WorkShiftTimeSlotItem,
        "startDate" | "endDate" | "isCustomType" | "salaryRowTypeId" | "timeZoneName"
    >,
    t: TFunction
): { field: string; error: string }[] {
    const errors: { field: string; error: string }[] = [];
    if (!row.startDate) {
        errors.push({
            field: "startDate",
            error: t("workTime.startDateRequired"),
        });
    }
    if (!row.endDate) {
        errors.push({
            field: "endDate",
            error: t("workTime.endDateRequired"),
        });
    }
    if (
        row.startDate &&
        row.endDate &&
        Base.isBefore(row.endDate, row.startDate)
    ) {
        errors.push({
            field: "endDate",
            error: t("workTime.endTimeBeforeStartTime"),
        });
    }
    if (!row.timeZoneName) {
        errors.push({
            field: "timeZoneName",
            error: t("workTime.timeZoneRequired"),
        });
    }
    if (row.isCustomType && !row.salaryRowTypeId) {
        errors.push({
            field: "salaryRowTypeId",
            error: t("workTime.salaryRowTypeRequired"),
        });
    }
    return errors;
}
